/**
 * AlbumPermissions Component
 * Modal for managing album access permissions
 * Mobile-optimized with stacked layouts
 */

import React, { useState, useEffect } from 'react';
import { useTeam } from '../contexts/TeamContext';
import { FiX, FiUsers, FiEye, FiPlus, FiEdit2, FiShield, FiTrash2, FiCheck, FiGlobe, FiChevronDown } from 'react-icons/fi';

const PERMISSION_LEVELS = {
  view: { label: 'View', icon: FiEye, description: 'Can view photos' },
  add: { label: 'Add', icon: FiPlus, description: 'Can add photos' },
  edit: { label: 'Edit', icon: FiEdit2, description: 'Can edit and remove photos' },
  admin: { label: 'Admin', icon: FiShield, description: 'Full control' }
};

// Permission level badge
function PermissionBadge({ level, size = 'sm' }) {
  const config = PERMISSION_LEVELS[level] || PERMISSION_LEVELS.view;
  const Icon = config.icon;

  const colors = {
    view: 'bg-gray-100 text-gray-700',
    add: 'bg-green-100 text-green-700',
    edit: 'bg-blue-100 text-blue-700',
    admin: 'bg-blue-100 text-blue-700'
  };

  const sizeClasses = size === 'lg'
    ? 'px-3 py-1.5 text-sm'
    : 'px-2 py-0.5 text-xs';

  return (
    <span className={`inline-flex items-center gap-1.5 font-medium rounded-full ${colors[level] || colors.view} ${sizeClasses}`}>
      <Icon size={size === 'lg' ? 16 : 12} />
      {config.label}
    </span>
  );
}

// Permission selector - mobile optimized with larger touch targets
function PermissionSelector({ value, onChange, disabled, fullWidth = false }) {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <div className={`relative ${fullWidth ? 'w-full' : ''}`}>
      <button
        onClick={() => !disabled && setIsOpen(!isOpen)}
        disabled={disabled}
        className={`flex items-center justify-between gap-2 px-3 py-2.5 text-sm border border-gray-200 rounded-lg hover:bg-gray-50 disabled:opacity-50 ${fullWidth ? 'w-full' : ''}`}
      >
        <PermissionBadge level={value} />
        <FiChevronDown size={16} className={`text-gray-400 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>

      {isOpen && (
        <>
          <div className="fixed inset-0 z-10" onClick={() => setIsOpen(false)} />
          <div className={`absolute z-20 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg ${fullWidth ? 'left-0 right-0' : 'right-0 min-w-48'}`}>
            {Object.entries(PERMISSION_LEVELS).map(([level, config]) => (
              <button
                key={level}
                onClick={() => {
                  onChange(level);
                  setIsOpen(false);
                }}
                className={`w-full px-4 py-3 text-left hover:bg-gray-50 first:rounded-t-lg last:rounded-b-lg ${level === value ? 'bg-blue-50' : ''}`}
              >
                <div className="flex items-center justify-between">
                  <PermissionBadge level={level} />
                  {level === value && <FiCheck size={18} className="text-blue-500" />}
                </div>
                <p className="mt-1 text-sm text-gray-500">{config.description}</p>
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

// User permission row - mobile optimized with stacked layout
function UserPermissionRow({ permission, onUpdate, onRemove, disabled }) {
  const [updating, setUpdating] = useState(false);

  const handleUpdate = async (newLevel) => {
    if (permission.permission_level === newLevel) return;

    setUpdating(true);
    try {
      await onUpdate(permission.user_id, newLevel);
    } finally {
      setUpdating(false);
    }
  };

  const handleRemove = async () => {
    if (!confirm(`Remove ${permission.display_name || permission.email}'s access to this album?`)) return;

    setUpdating(true);
    try {
      await onRemove(permission.user_id);
    } finally {
      setUpdating(false);
    }
  };

  return (
    <div className="p-3 border-b border-gray-100 last:border-0">
      {/* Mobile: stacked layout, Desktop: side by side */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="min-w-0">
          <p className="font-medium text-gray-900 truncate">
            {permission.display_name || permission.email?.split('@')[0]}
          </p>
          {permission.email && (
            <p className="text-sm text-gray-500 truncate">{permission.email}</p>
          )}
        </div>

        <div className="flex items-center gap-2">
          <div className="flex-1 sm:flex-none">
            <PermissionSelector
              value={permission.permission_level}
              onChange={handleUpdate}
              disabled={disabled || updating}
              fullWidth
            />
          </div>
          <button
            onClick={handleRemove}
            disabled={disabled || updating}
            className="p-2.5 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg disabled:opacity-50 shrink-0"
            title="Remove access"
          >
            <FiTrash2 size={18} />
          </button>
        </div>
      </div>
    </div>
  );
}

// Add user permission form - mobile optimized with stacked layout
function AddPermissionForm({ teamMembers, existingUserIds, onAdd, disabled }) {
  const [selectedUserId, setSelectedUserId] = useState('');
  const [permissionLevel, setPermissionLevel] = useState('view');
  const [adding, setAdding] = useState(false);

  const availableMembers = teamMembers.filter(m => !existingUserIds.includes(m.user_id));

  const handleAdd = async () => {
    if (!selectedUserId) return;

    setAdding(true);
    try {
      await onAdd(selectedUserId, permissionLevel);
      setSelectedUserId('');
      setPermissionLevel('view');
    } finally {
      setAdding(false);
    }
  };

  if (availableMembers.length === 0) {
    return (
      <div className="p-4 text-sm text-center text-gray-500 bg-gray-50 rounded-lg">
        All team members already have access
      </div>
    );
  }

  return (
    <div className="p-4 space-y-3 bg-gray-50 rounded-lg">
      {/* Team member select - full width */}
      <select
        value={selectedUserId}
        onChange={(e) => setSelectedUserId(e.target.value)}
        disabled={disabled || adding}
        className="w-full px-3 py-3 text-base border border-gray-200 rounded-lg bg-white appearance-none"
      >
        <option value="">Select team member...</option>
        {availableMembers.map(member => (
          <option key={member.user_id} value={member.user_id}>
            {member.display_name || member.email?.split('@')[0]}
            {member.email && ` (${member.email})`}
          </option>
        ))}
      </select>

      {/* Permission level and Add button row */}
      <div className="flex gap-2">
        <div className="flex-1">
          <PermissionSelector
            value={permissionLevel}
            onChange={setPermissionLevel}
            disabled={disabled || adding}
            fullWidth
          />
        </div>
        <button
          onClick={handleAdd}
          disabled={!selectedUserId || disabled || adding}
          className="flex items-center justify-center gap-2 px-4 py-2.5 text-sm font-medium text-white bg-blue-500 rounded-lg hover:bg-blue-600 disabled:opacity-50 shrink-0"
        >
          <FiPlus size={18} />
          <span className="hidden sm:inline">Add</span>
        </button>
      </div>
    </div>
  );
}

// Main AlbumPermissions modal component
export default function AlbumPermissions({ albumId, albumTitle, isTeamVisible, onClose, onUpdate }) {
  const { teamMembers, isTeamAdmin, currentUserId } = useTeam();
  const [permissions, setPermissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [teamVisible, setTeamVisible] = useState(isTeamVisible);
  const [updatingVisibility, setUpdatingVisibility] = useState(false);
  const [albumOwnerId, setAlbumOwnerId] = useState(null);

  // Fetch album permissions
  useEffect(() => {
    async function loadPermissions() {
      try {
        const res = await fetch(`/api/albums/${albumId}/permissions`, { credentials: 'include' });
        if (res.ok) {
          const data = await res.json();
          setPermissions(data.permissions || []);
          setAlbumOwnerId(data.owner_id);
        } else if (res.status === 403) {
          setError('You do not have permission to manage this album');
        } else {
          throw new Error('Failed to load permissions');
        }
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    }

    loadPermissions();
  }, [albumId]);

  // User can manage permissions if they own the album or are a team admin
  const canManagePermissions = albumOwnerId === currentUserId || isTeamAdmin;

  // Toggle team visibility
  const handleToggleTeamVisible = async () => {
    setUpdatingVisibility(true);
    try {
      const res = await fetch(`/api/albums/${albumId}/team-visible`, {
        method: 'PATCH',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ team_visible: !teamVisible })
      });

      if (res.ok) {
        setTeamVisible(!teamVisible);
        onUpdate?.();
      }
    } catch (err) {
      console.error('Failed to update visibility:', err);
    } finally {
      setUpdatingVisibility(false);
    }
  };

  // Add permission
  const handleAddPermission = async (userId, level) => {
    try {
      const res = await fetch(`/api/albums/${albumId}/permissions`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ user_id: userId, permission_level: level })
      });

      if (res.ok) {
        // Find the team member to get their display info
        const member = teamMembers.find(m => m.user_id === userId);
        setPermissions(prev => [...prev, {
          user_id: userId,
          permission_level: level,
          display_name: member?.display_name,
          email: member?.email
        }]);
        onUpdate?.();
      }
    } catch (err) {
      console.error('Failed to add permission:', err);
    }
  };

  // Update permission
  const handleUpdatePermission = async (userId, level) => {
    try {
      const res = await fetch(`/api/albums/${albumId}/permissions/${userId}`, {
        method: 'PATCH',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ permission_level: level })
      });

      if (res.ok) {
        setPermissions(prev =>
          prev.map(p => p.user_id === userId ? { ...p, permission_level: level } : p)
        );
        onUpdate?.();
      }
    } catch (err) {
      console.error('Failed to update permission:', err);
    }
  };

  // Remove permission
  const handleRemovePermission = async (userId) => {
    try {
      const res = await fetch(`/api/albums/${albumId}/permissions/${userId}`, {
        method: 'DELETE',
        credentials: 'include'
      });

      if (res.ok) {
        setPermissions(prev => prev.filter(p => p.user_id !== userId));
        onUpdate?.();
      }
    } catch (err) {
      console.error('Failed to remove permission:', err);
    }
  };

  const existingUserIds = permissions.map(p => p.user_id);

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/50" onClick={onClose}>
      <div
        className="w-full sm:max-w-lg max-h-[95vh] sm:max-h-[90vh] overflow-auto bg-white rounded-t-2xl sm:rounded-xl shadow-xl"
        onClick={e => e.stopPropagation()}
      >
        {/* Header - sticky on mobile */}
        <div className="sticky top-0 z-10 flex items-center justify-between p-4 bg-white border-b border-gray-200">
          <div className="min-w-0 pr-4">
            <h2 className="text-lg font-semibold">Album Permissions</h2>
            <p className="text-sm text-gray-500 truncate">{albumTitle}</p>
          </div>
          <button
            onClick={onClose}
            className="p-2.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg shrink-0"
          >
            <FiX size={22} />
          </button>
        </div>

        {/* Content */}
        <div className="p-4 space-y-4">
          {error ? (
            <div className="p-4 text-center text-red-600 bg-red-50 rounded-lg">
              {error}
            </div>
          ) : loading ? (
            <div className="flex items-center justify-center p-8">
              <div className="w-8 h-8 border-2 border-blue-500 rounded-full animate-spin border-t-transparent" />
            </div>
          ) : (
            <>
              {/* Team visibility toggle */}
              <div className="p-4 bg-gray-50 rounded-lg">
                <div className="flex items-start sm:items-center justify-between gap-4">
                  <div className="flex items-start sm:items-center gap-3">
                    <FiGlobe size={22} className="text-gray-600 shrink-0 mt-0.5 sm:mt-0" />
                    <div>
                      <p className="font-medium">Team Visibility</p>
                      <p className="text-sm text-gray-500">
                        Allow all team members to view
                      </p>
                    </div>
                  </div>
                  <button
                    onClick={handleToggleTeamVisible}
                    disabled={updatingVisibility || !canManagePermissions}
                    className={`relative w-14 h-8 rounded-full transition-colors shrink-0 ${
                      teamVisible ? 'bg-blue-500' : 'bg-gray-300'
                    } disabled:opacity-50`}
                  >
                    <span
                      className={`absolute top-1 w-6 h-6 bg-white rounded-full shadow transition-transform ${
                        teamVisible ? 'left-7' : 'left-1'
                      }`}
                    />
                  </button>
                </div>
              </div>

              {/* Individual permissions */}
              <div>
                <div className="flex items-center gap-2 mb-3">
                  <FiUsers size={20} className="text-gray-600" />
                  <h3 className="font-medium">Individual Permissions</h3>
                </div>

                <div className="border border-gray-200 rounded-lg overflow-hidden">
                  {permissions.length === 0 ? (
                    <div className="p-6 text-sm text-center text-gray-500">
                      No individual permissions set
                    </div>
                  ) : (
                    permissions.map(permission => (
                      <UserPermissionRow
                        key={permission.user_id}
                        permission={permission}
                        onUpdate={handleUpdatePermission}
                        onRemove={handleRemovePermission}
                        disabled={!canManagePermissions}
                      />
                    ))
                  )}
                </div>
              </div>

              {/* Add permission form */}
              {canManagePermissions && (
                <div>
                  <h3 className="mb-3 font-medium">Add Permission</h3>
                  <AddPermissionForm
                    teamMembers={teamMembers}
                    existingUserIds={existingUserIds}
                    onAdd={handleAddPermission}
                    disabled={false}
                  />
                </div>
              )}
            </>
          )}
        </div>

        {/* Footer - sticky on mobile */}
        <div className="sticky bottom-0 flex justify-end p-4 bg-white border-t border-gray-200">
          <button
            onClick={onClose}
            className="w-full sm:w-auto px-6 py-3 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
          >
            Done
          </button>
        </div>
      </div>
    </div>
  );
}
